"use client";

import { AnimatePresence, motion, type Variants } from "framer-motion";
import { Container } from "@/components/ui/Container";
import { Button } from "@/components/ui/Button";
import { HeroVideoBackground } from "@/components/sections/HeroVideoBackground";
import { OpenSignupButton } from "@/components/popups/OpenSignupButton";
import { credibilityLine } from "@/content/metrics";
import { useAutoAdvance, usePrefersReducedMotion } from "@/lib/motion";

const container: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.1 } },
};

const item: Variants = {
  hidden: { opacity: 0, y: 22 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } },
};

const heroSlides = [
  {
    lead: "Cloud, streaming e monitoramento para provedores,",
    highlight: "com estabilidade para crescer.",
    description:
      "Integre CDN, Media Server, IPTV, OTT, VOD e infraestrutura cloud em uma operação acompanhada de ponta a ponta pela SAT Labs.",
  },
  {
    lead: "Antecipe falhas na sua operação,",
    highlight: "antes que afetem o assinante.",
    description:
      "Centralize o monitoramento, elimine pontos cegos e reduza o tempo de resposta a incidentes.",
  },
  {
    lead: "Menos fornecedores desconectados,",
    highlight: "mais controle da operação.",
    description:
      "Conecte conteúdo, infraestrutura, dados e suporte técnico sob uma única gestão especializada.",
  },
] as const;

export function Hero() {
  const reduced = usePrefersReducedMotion();
  const [activeSlide, setActiveSlide] = useAutoAdvance(heroSlides.length, 5000);
  const slide = heroSlides[activeSlide];

  return (
    <section
      data-nano-scene="hero"
      // -mt-18 cancela o pt-18 que o <main> ganhou pra compensar o header
      // (agora fixed) — só o Hero precisa ficar atrás do header no topo.
      className="relative -mt-18 flex min-h-[640px] items-center overflow-hidden py-28 sm:min-h-[760px] sm:py-36"
    >
      <HeroVideoBackground />

      <Container className="relative flex flex-col items-center text-center">
        <motion.div
          initial={reduced ? undefined : "hidden"}
          animate={reduced ? undefined : "show"}
          variants={container}
          className="flex flex-col items-center"
        >
          <motion.div variants={item} className="flex min-h-[310px] w-full flex-col items-center justify-center sm:min-h-[330px]">
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={activeSlide}
                initial={reduced ? { opacity: 0 } : { opacity: 0, x: 32 }}
                animate={{ opacity: 1, x: 0 }}
                exit={reduced ? { opacity: 0 } : { opacity: 0, x: -32 }}
                transition={{ duration: reduced ? 0.15 : 0.55, ease: [0.16, 1, 0.3, 1] }}
                className="flex flex-col items-center"
              >
                <h1 className="max-w-4xl text-4xl font-black uppercase italic leading-[0.95] tracking-tight text-text-secondary sm:text-6xl lg:text-7xl">
                  {slide.lead}{" "}
                  <span
                    className="text-accent-lime"
                    style={{ textShadow: "0 0 24px rgba(153,255,0,0.35)" }}
                  >
                    {slide.highlight}
                  </span>
                </h1>

                <p className="mt-8 max-w-2xl text-lg text-text-secondary sm:text-xl">
                  {slide.description}
                </p>
              </motion.div>
            </AnimatePresence>
          </motion.div>

          <motion.div
            variants={item}
            className="mt-3 flex items-center justify-center gap-3"
            role="group"
            aria-label="Selecionar mensagem do destaque"
          >
            {heroSlides.map((heroSlide, index) => (
              <button
                key={heroSlide.lead}
                type="button"
                onClick={() => setActiveSlide(index)}
                aria-label={`Exibir mensagem ${index + 1}`}
                aria-current={activeSlide === index ? "true" : undefined}
                className={`h-1.5 rounded-full transition-all duration-300 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-accent-lime ${
                  activeSlide === index
                    ? "w-10 bg-accent-lime"
                    : "w-5 bg-text-primary/30 hover:bg-text-primary/60"
                }`}
              >
                <span className="sr-only">
                  {activeSlide === index ? "Mensagem atual: " : ""}
                  {heroSlide.lead}
                </span>
              </button>
            ))}
          </motion.div>

          <motion.div variants={item} className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <OpenSignupButton size="lg">Solicitar diagnóstico</OpenSignupButton>
            <Button href="#solucoes" variant="secondary" size="lg">
              Ver soluções
            </Button>
          </motion.div>

          <motion.p
            variants={item}
            className="mt-10 font-mono text-xs font-semibold uppercase tracking-[0.12em] text-text-primary/80"
          >
            {credibilityLine}
          </motion.p>
        </motion.div>
      </Container>
    </section>
  );
}
